import React, { useEffect, useState, useCallback } from "react";
import { Button, Modal, Spinner, Box } from "@amzn/awsui-components-react-v3";
import { useHistory } from "react-router-dom";
import {
  FieldSchemaDto,
  FieldGroupDto,
} from "@amzn/crowd-front-end-common-library";
import { MetricsState } from "../logging";
import { useHttpRequest, LoadingState } from "../http";
import TeamList, { TeamListItem } from "./TeamList";
import { FormContextProvider } from "../forms/formContext";
import FormFieldRenderer from "../forms/FormFieldRenderer/FormFieldRenderer";
import deleteTeamFormSchema from "./deleteTeamFormSchema";
import styles from "./deleteTeam.module.css";
import { DisplayBanner } from "../banner/bannerSlice";
import { teamAfterFirstFailureBanner } from "../common/utils/ErrorBannerMessages";
import { BannerComponentId } from "../banner/bannerComponentId";
import {
  createDeleteTeamsWorkflowSuccess,
  deleteTeamAccepted,
  deleteTeamSuccess,
} from "./deleteTeamBannerMessage";
import { HttpStatus } from "../http/httpStatus";
import { GetSubheaderDetailFetchPayload } from "../layout/subheader/subheaderSlice";

const deleteText = "delete";

export interface DeleteTeamProps {
  teamId: string;
  teamName: string;
  showModal: boolean;
  parentTeamId: string | undefined;
}

export interface DeleteTeamModalProps {
  setShowModal: (visible: boolean) => void;
  teamChildren: TeamListItem[];
  parentTeamName: string;
  isLoading: boolean;
  metrics: MetricsState;
  setBanner: (banner: DisplayBanner | undefined) => void;
  getSubheaderDetailFetch: (payload: GetSubheaderDetailFetchPayload) => void;
}

interface DeleteFrictionResult {
  frictionInputValue: string;
}

function getDeleteField(): FieldSchemaDto {
  const fieldGroup = deleteTeamFormSchema.groups.find(
    (group) => group.groupLabel === "Delete team"
  ) as FieldGroupDto;
  const frictionField = fieldGroup.fieldDefinitions.find(
    (field) => field.propertyName === "frictionInputValue"
  ) as FieldSchemaDto;

  return frictionField;
}

// Builds the modal content when the team has child teams.
function getDeleteInfoWithChildren(
  teamName: string,
  parentTeamName: string,
  teamListItems: TeamListItem[]
): JSX.Element {
  let subteamsText: JSX.Element;
  if (teamListItems.length > 1) {
    const boldSubTeamsText = (
      <strong>{`${teamListItems.length} sub teams`}</strong>
    );
    subteamsText = (
      <div>
        <div className={styles["sub-teams-text-background"]}>
          <div className={styles["sub-teams-text"]}>
            The following {boldSubTeamsText} will be moved to team{" "}
            {parentTeamName}:
          </div>
        </div>
      </div>
    );
  } else {
    subteamsText = (
      <div>The following sub team will be moved to team {parentTeamName}:</div>
    );
  }

  return (
    <div>
      <div data-testid="modal-content-children-info">{subteamsText}</div>
      <div className={styles["modal-content-children"]}>
        <TeamList teams={teamListItems} />
      </div>
    </div>
  );
}

const DeleteTeamModal: React.FC<DeleteTeamProps & DeleteTeamModalProps> = ({
  teamId,
  teamName,
  showModal,
  setShowModal,
  teamChildren,
  parentTeamName,
  parentTeamId,
  isLoading,
  metrics,
  setBanner,
  getSubheaderDetailFetch,
}): JSX.Element => {
  const [submitEnabled, setSubmitEnabled] = useState<boolean>(false);

  const onFrictionChange = useCallback(
    (_: Partial<DeleteFrictionResult>, diff: Partial<DeleteFrictionResult>) => {
      const shouldEnableSubmit = diff.frictionInputValue
        ? diff.frictionInputValue.toLowerCase() === deleteText
        : false;
      setSubmitEnabled(shouldEnableSubmit);
    },
    []
  );

  const { loadingState, sendRequest, response } = useHttpRequest(
    "deleteTeams",
    metrics
  );
  const history = useHistory();
  useEffect(() => {
    if (loadingState === LoadingState.RESOLVED) {
      if (response && response.status === HttpStatus.Created) {
        setBanner(createDeleteTeamsWorkflowSuccess(teamName));
      } else if (response && response.status === HttpStatus.Accepted) {
        setBanner(deleteTeamAccepted(teamName));
      } else {
        setBanner(deleteTeamSuccess(teamName));
      }

      /**
       * Reload page if the user is already looking at the parent team's page
       * otherwise, navigate user back to parent team's page
       */
      if (history.location.pathname.includes(parentTeamId as string)) {
        getSubheaderDetailFetch({ id: parentTeamId as string });
      } else {
        history.push(`/team/${parentTeamId}`);
      }
    } else if (loadingState === LoadingState.REJECTED) {
      setBanner(
        teamAfterFirstFailureBanner(
          "delete",
          BannerComponentId.DeleteTeamFailure
        )
      );
      setShowModal(false);
    }
  }, [
    setBanner,
    loadingState,
    teamName,
    setShowModal,
    parentTeamId,
    response,
    history,
    getSubheaderDetailFetch,
  ]);

  let deleteInfo: JSX.Element = <div />;
  let deleteFriction: JSX.Element = <div />;
  //TODO: Error states here?  Need messaging from UX.
  if (!isLoading) {
    const deleteFieldLabel = (
      <span>
        To confirm deletion, type <i>delete</i> in the field
      </span>
    );
    const childTeamContentStyle =
      teamChildren && teamChildren.length > 0
        ? "child-team-content-padding"
        : "";
    deleteFriction = (
      <div className={styles[childTeamContentStyle as keyof typeof styles]}>
        <Box padding={{ vertical: "s" }}>
          <FormFieldRenderer
            field={getDeleteField()}
            customLabel={deleteFieldLabel}
            stretch
            dispatchOnValueUpdate
          />
        </Box>
      </div>
    );

    if (teamChildren && teamChildren.length > 0) {
      deleteInfo = getDeleteInfoWithChildren(
        teamName,
        parentTeamName,
        teamChildren
      );
    }
  }

  const modalContent: JSX.Element = !isLoading ? (
    <div>
      {deleteFriction}
      {deleteInfo}
    </div>
  ) : (
    <Spinner />
  );

  const onSubmit = (): void => {
    sendRequest({
      url: `/api/v1/teams/${teamId}`,
      method: "DELETE",
    });
  };

  const modalFooter: JSX.Element = (
    <div
      className={`${styles["modal-buttons-holder"]} awsui-util-mr-xl awsui-util-pr-s awsui-util-m-xs`}
    >
      <Button
        ariaLabel="delete team modal close"
        variant="link"
        onClick={() => setShowModal(false)}
      >
        Cancel
      </Button>
      <Button
        data-test-id="delete-team-submit"
        ariaLabel="delete team modal delete"
        variant="primary"
        loading={loadingState === LoadingState.LOADING}
        disabled={!submitEnabled}
        onClick={() => onSubmit()}
      >
        Delete team
      </Button>
    </div>
  );
  return showModal ? (
    <FormContextProvider<Partial<DeleteFrictionResult>>
      metrics={metrics}
      formId="deleteteam-modal"
      onFormValueChange={onFrictionChange}
    >
      <div className={styles["centered-modal"]}>
        <Modal
          visible
          header={`Delete ${teamName}`}
          footer={modalFooter}
          onDismiss={() => setShowModal(false)}
        >
          {modalContent}
        </Modal>
      </div>
    </FormContextProvider>
  ) : (
    <div />
  );
};

export default DeleteTeamModal;
